import React from 'react';
import HeroCarousel from '../HeroCarousel';
import MaterialsSection from './MaterialsSection';
import AppleStyleGrid from '../AppleStyleGrid';
import TechSection from './TechSection';
import RecentlyViewed from '../RecentlyViewed';
import UnboxingSection from './UnboxingSection';
import { GlobalReviewCarousel } from '../reviews/GlobalReviewCarousel';
import { SocialVideoCarousel } from '../social/SocialVideoCarousel';
import { SlidersHorizontal } from 'lucide-react';
import { DiscoverSettings } from '../../services/DiscoverService';
import { Branch, Product } from '../../types';

interface HomeViewProps {
    discoverSettings: DiscoverSettings | null;
    currentBranch: Branch;
    recentlyViewed: Product[];
    onSelectMaterial: (material: 'carbon' | 'forged') => void;
    onSelectBrand: (brand: string) => void;
    onProductSelect: (product: Product) => void;
    onViewAllReviews: () => void;
    onOpenSearchAssistant: () => void;
    onKeywordSearch: (query: string) => void;
}

const HomeView: React.FC<HomeViewProps> = ({
    discoverSettings,
    currentBranch,
    recentlyViewed,
    onSelectMaterial,
    onSelectBrand,
    onProductSelect,
    onViewAllReviews,
    onOpenSearchAssistant,
    onKeywordSearch
}) => {
    return (
        <>
            <HeroCarousel />

            {/* Global Search & Assistant Section */}
            <div className="bg-zinc-900/50 border-b border-zinc-800 py-10 sm:py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto space-y-8">
                        {/* Direct Keyword Search */}
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <SlidersHorizontal className="h-5 w-5 text-zinc-500 group-focus-within:text-white transition-colors" />
                            </div>
                            <input
                                id="search-input-main"
                                type="text"
                                placeholder="Busca tu case o accesorio (ej: iPhone 15 Pro)..."
                                className="w-full bg-black border border-zinc-800 focus:border-white focus:ring-2 focus:ring-white/50 outline-none text-white py-5 pl-12 pr-32 rounded-none transition-all font-sans text-base sm:text-lg"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onKeywordSearch(e.currentTarget.value);
                                    }
                                }}
                            />
                            <button
                                id="btn-search-main"
                                onClick={(e) => {
                                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                                    onKeywordSearch(input.value);
                                }}
                                className="absolute right-2 top-2 bottom-2 bg-white hover:bg-zinc-200 text-black font-display font-black uppercase text-xs sm:text-sm px-6 transition-all tracking-widest active:scale-95 shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                            >
                                Buscar
                            </button>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="h-px flex-grow bg-zinc-800" />
                            <span className="text-[10px] sm:text-xs font-mono font-bold text-zinc-600 uppercase tracking-widest">O encentra tu modelo ideal</span>
                            <div className="h-px flex-grow bg-zinc-800" />
                        </div>
                    </div>

                    {/* Search Assistant Trigger Banner */}
                    <div
                        onClick={onOpenSearchAssistant}
                        className="bg-zinc-950 border border-zinc-800 hover:border-white p-6 md:p-8 cursor-pointer group transition-all duration-300 relative overflow-hidden rounded-sm mt-8"
                    >
                        {/* Animated Glow */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />

                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-6">
                                <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800 group-hover:border-white transition-colors skew-x-[-6deg]">
                                    <div className="skew-x-[6deg]">
                                        <SlidersHorizontal className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl md:text-2xl font-display font-bold uppercase text-white group-hover:text-white transition-colors">
                                        ¿Buscas protección para tu dispositivo?
                                    </h3>
                                    <p className="text-gray-500 text-sm mt-1">
                                        Filtra por: <span className="text-white">Marca</span> &gt; <span className="text-white">Modelo</span>
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-bold uppercase tracking-widest text-zinc-500 group-hover:text-white transition-colors">Iniciar Búsqueda</span>
                                <div className="w-8 h-8 rounded-full border border-zinc-700 flex items-center justify-center group-hover:bg-white group-hover:border-white group-hover:text-black transition-all">
                                    <span className="text-lg leading-none mb-0.5">→</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Materials Narrative */}
            {discoverSettings && (
                <MaterialsSection 
                    settings={discoverSettings} 
                    onSelectMaterial={onSelectMaterial}
                />
            )}

            {/* Main Brand Grid */}
            <AppleStyleGrid onSelectBrand={onSelectBrand} />

            {/* Tech Section */}
            {discoverSettings && <TechSection settings={discoverSettings} />}

            {/* Recently Viewed Section */}
            <RecentlyViewed
                products={recentlyViewed}
                currentBranch={currentBranch}
                onProductClick={onProductSelect}
            />

            {/* Final Experience & CTA */}
            {discoverSettings && <UnboxingSection settings={discoverSettings} onNavigate={onSelectBrand} />}
            
            {/* Global Reviews */}
            <GlobalReviewCarousel onViewAll={onViewAllReviews} />

            <SocialVideoCarousel />
        </>
    );
};

export default HomeView;
